import { Link } from "@tanstack/solid-router";
import { For, Show, createEffect, createSignal, onCleanup, onMount } from "solid-js";

import { HighlightMatch } from "~/components/HighlightMatch";
import { useSearch } from "~/lib/hooks";
import type { AttachableEntity } from "~/lib/types";

type Props = {
  wsKey: () => string;
};

/**
 * Header search box. Debounced 250ms; opens a popover with sectioned hits.
 * Esc closes; click-outside closes.
 *
 * Notes & activities link to whichever entity they're attached to —
 * including custom records via the `attached_object_key` / `primary_object_key`
 * field the backend now joins in.
 */
export function GlobalSearch(props: Props) {
  const [raw, setRaw] = createSignal("");
  const [debounced, setDebounced] = createSignal("");
  const [open, setOpen] = createSignal(false);
  let rootRef: HTMLDivElement | undefined;

  // Debounce keystrokes — TanStack Query would otherwise fire a request on
  // every character.
  createEffect(() => {
    const r = raw();
    const t = window.setTimeout(() => setDebounced(r), 250);
    onCleanup(() => clearTimeout(t));
  });

  const results = useSearch(props.wsKey, debounced);

  // Close on click outside or Esc. Mount in onMount so we don't run on SSR
  // and so the listener gets removed cleanly.
  onMount(() => {
    const onClick = (e: MouseEvent) => {
      if (!rootRef) return;
      if (!rootRef.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("click", onClick);
    window.addEventListener("keydown", onKey);
    onCleanup(() => {
      window.removeEventListener("click", onClick);
      window.removeEventListener("keydown", onKey);
    });
  });

  const close = () => {
    setOpen(false);
  };

  const hasAny = () => {
    const r = results.data;
    if (!r) return false;
    return (
      r.accounts.length > 0 ||
      r.contacts.length > 0 ||
      r.custom_records.length > 0 ||
      r.notes.length > 0 ||
      r.activities.length > 0
    );
  };

  return (
    <div ref={rootRef} class="relative">
      <input
        type="search"
        class="input input-bordered input-sm w-48 sm:w-72"
        placeholder="Search…"
        aria-label="Search workspace"
        value={raw()}
        onInput={(e) => {
          setRaw(e.currentTarget.value);
          setOpen(true);
        }}
        onFocus={() => debounced().length > 0 && setOpen(true)}
      />
      <Show when={open() && debounced().length > 0}>
        <div
          class="absolute right-0 mt-1 z-20 w-[min(28rem,90vw)] max-h-[60vh] overflow-y-auto rounded-lg bg-base-100 shadow-lg border border-base-300"
          role="listbox"
          aria-label="Search results"
        >
          <Show when={results.isLoading}>
            <div class="px-3 py-3 text-sm opacity-60">Searching…</div>
          </Show>
          <Show when={!results.isLoading && results.data && !hasAny()}>
            <div class="px-3 py-3 text-sm opacity-60">No matches for "{debounced()}".</div>
          </Show>

          <Show when={(results.data?.accounts.length ?? 0) > 0}>
            <Section label="Accounts">
              <For each={results.data?.accounts}>
                {(a) => (
                  <Link
                    to="/workspaces/$wsKey/accounts/$accId"
                    params={{ wsKey: props.wsKey(), accId: a.id }}
                    class="block px-3 py-2 hover:bg-base-200"
                    onClick={close}
                  >
                    <div class="font-medium">
                      <HighlightMatch text={a.name} query={debounced()} />
                    </div>
                    <Show when={a.primary_domain || a.primary_email}>
                      <div class="text-xs opacity-60 truncate">
                        <HighlightMatch
                          text={a.primary_domain ?? a.primary_email ?? ""}
                          query={debounced()}
                        />
                      </div>
                    </Show>
                  </Link>
                )}
              </For>
            </Section>
          </Show>

          <Show when={(results.data?.contacts.length ?? 0) > 0}>
            <Section label="Contacts">
              <For each={results.data?.contacts}>
                {(c) => (
                  <Link
                    to="/workspaces/$wsKey/contacts/$conId"
                    params={{ wsKey: props.wsKey(), conId: c.id }}
                    class="block px-3 py-2 hover:bg-base-200"
                    onClick={close}
                  >
                    <div class="font-medium">
                      <HighlightMatch text={c.full_name} query={debounced()} />
                    </div>
                    <Show when={c.primary_email}>
                      <div class="text-xs opacity-60 truncate">
                        <HighlightMatch text={c.primary_email ?? ""} query={debounced()} />
                      </div>
                    </Show>
                  </Link>
                )}
              </For>
            </Section>
          </Show>

          <Show when={(results.data?.custom_records.length ?? 0) > 0}>
            <Section label="Custom records">
              <For each={results.data?.custom_records}>
                {(r) => (
                  <Link
                    to="/workspaces/$wsKey/custom/$objectKey/$recordId"
                    params={{
                      wsKey: props.wsKey(),
                      objectKey: r.object_key,
                      recordId: r.id,
                    }}
                    class="block px-3 py-2 hover:bg-base-200"
                    onClick={close}
                  >
                    <div class="font-medium">
                      <HighlightMatch text={r.display_name || r.name} query={debounced()} />
                    </div>
                    <div class="text-xs opacity-60">{r.object_label_singular}</div>
                  </Link>
                )}
              </For>
            </Section>
          </Show>

          <Show when={(results.data?.notes.length ?? 0) > 0}>
            <Section label="Notes">
              <For each={results.data?.notes}>
                {(n) => (
                  <AttachedHit
                    wsKey={props.wsKey}
                    query={debounced()}
                    entityType={n.attached_to_entity_type}
                    entityId={n.attached_to_entity_id}
                    objectKey={n.attached_object_key}
                    title={n.body}
                    subtitle="note"
                    onClick={close}
                  />
                )}
              </For>
            </Section>
          </Show>

          <Show when={(results.data?.activities.length ?? 0) > 0}>
            <Section label="Activities">
              <For each={results.data?.activities}>
                {(a) => (
                  <AttachedHit
                    wsKey={props.wsKey}
                    query={debounced()}
                    entityType={a.primary_entity_type}
                    entityId={a.primary_entity_id}
                    objectKey={a.primary_object_key}
                    title={a.subject}
                    body={a.body}
                    subtitle={a.kind}
                    onClick={close}
                  />
                )}
              </For>
            </Section>
          </Show>

          <Show when={hasAny()}>
            <div class="border-t border-base-200 px-3 py-2">
              <Link
                to="/workspaces/$wsKey/search"
                params={{ wsKey: props.wsKey() }}
                search={{ q: debounced() }}
                class="link link-primary text-sm"
                onClick={close}
              >
                See all matches for "{debounced()}" →
              </Link>
            </div>
          </Show>
        </div>
      </Show>
    </div>
  );
}

function Section(props: { label: string; children: any }) {
  return (
    <div class="border-b border-base-200 last:border-b-0">
      <div class="px-3 pt-2 pb-1 text-xs uppercase tracking-wide opacity-50">{props.label}</div>
      {props.children}
    </div>
  );
}

function AttachedHit(props: {
  wsKey: () => string;
  query: string;
  entityType: AttachableEntity;
  entityId: string;
  // Set when entityType === 'custom_record'; ignored otherwise.
  objectKey: string | null;
  // Primary line (note body or activity subject). Highlighted.
  title: string;
  // Optional second line (activity body). Highlighted when present.
  body?: string | null;
  subtitle: string;
  onClick: () => void;
}) {
  const inner = () => (
    <>
      <div class="font-medium truncate">
        <HighlightMatch text={props.title} query={props.query} />
      </div>
      <Show when={props.body}>
        {(b) => (
          <div class="text-xs opacity-70 truncate">
            <HighlightMatch text={b()} query={props.query} />
          </div>
        )}
      </Show>
      <div class="text-xs opacity-60">
        {props.subtitle} · on {entityNoun(props.entityType)}
      </div>
    </>
  );

  if (props.entityType === "account") {
    return (
      <Link
        to="/workspaces/$wsKey/accounts/$accId"
        params={{ wsKey: props.wsKey(), accId: props.entityId }}
        class="block px-3 py-2 hover:bg-base-200"
        onClick={props.onClick}
      >
        {inner()}
      </Link>
    );
  }
  if (props.entityType === "contact") {
    return (
      <Link
        to="/workspaces/$wsKey/contacts/$conId"
        params={{ wsKey: props.wsKey(), conId: props.entityId }}
        class="block px-3 py-2 hover:bg-base-200"
        onClick={props.onClick}
      >
        {inner()}
      </Link>
    );
  }
  if (props.entityType === "deal") {
    return (
      <Link
        to="/workspaces/$wsKey/deals/$dealId"
        params={{ wsKey: props.wsKey(), dealId: props.entityId }}
        class="block px-3 py-2 hover:bg-base-200"
        onClick={props.onClick}
      >
        {inner()}
      </Link>
    );
  }
  if (props.entityType === "custom_record" && props.objectKey) {
    return (
      <Link
        to="/workspaces/$wsKey/custom/$objectKey/$recordId"
        params={{
          wsKey: props.wsKey(),
          objectKey: props.objectKey,
          recordId: props.entityId,
        }}
        class="block px-3 py-2 hover:bg-base-200"
        onClick={props.onClick}
      >
        {inner()}
      </Link>
    );
  }
  // Fallback for the (unexpected) case where a custom-record attachment
  // lacks its parent object key — render as a non-link so the row still
  // shows up in results.
  return <div class="block px-3 py-2 hover:bg-base-200">{inner()}</div>;
}

function entityNoun(et: AttachableEntity): string {
  if (et === "custom_record") return "custom record";
  return et;
}
